import React, { useEffect, useState } from "react";
import { LuArrowUpToLine } from "react-icons/lu";
import { RxReset } from "react-icons/rx";

import Button from "../../../components/Button";
import { useToaster } from "../../../components/toaster";
import { RokaApiError } from "../../../errors/RokaApiError";
import { useAdminApi } from "../../../hooks/api-hooks/useAdminApi";
import { Site } from "../../../types/rokaTypes";
import SiteResetTimeField from "./SiteResetTimeField";

interface SiteFormState {
  maximum: number;
  isDirty: boolean;
}

interface SitesAdminFormProps {
  site: Site;
  onChange?: (site: Site) => void;
}

const SitesAdminForm = ({ site, onChange }: SitesAdminFormProps) => {
  const { updateSite } = useAdminApi();
  const { showToaster } = useToaster();

  const [siteFormState, setSiteFormState] = useState<SiteFormState>({
    maximum: site.maximum,
    isDirty: false,
  });

  // Helper component for section headers
  const SectionHeader = ({
    icon: Icon,
    title,
  }: {
    icon: React.ComponentType;
    title: string;
  }) => (
    <h4>
      <span className="icon">
        <Icon />
      </span>
      {title}
    </h4>
  );

  // Helper function for updating form state
  const updateFormField = (
    field: keyof Omit<SiteFormState, "isDirty">,
    value: any,
  ) => {
    setSiteFormState((prevState) => ({
      ...prevState,
      [field]: value,
      isDirty: true,
    }));
  };

  // Helper function for resetting site form state
  const resetSiteFormState = () => ({
    maximum: site.maximum,
    isDirty: false,
  });

  // Helper function for error handling
  const handleError = (
    error: unknown,
    fallbackMessage: string,
    operationType: string,
  ) => {
    if (error instanceof RokaApiError) {
      showToaster(`${fallbackMessage}: ${error.message}`, {
        type: "error",
        errorId: error.requestId,
      });
    } else {
      showToaster(`En uventet feil oppstod under ${operationType}`, {
        type: "error",
      });
      console.error(`Error ${operationType}:`, error);
    }
  };

  // Reset form state when site changes
  useEffect(() => {
    setSiteFormState({
      maximum: site.maximum,
      isDirty: false,
    });
  }, [site]);

  const handleSaveSiteChanges = async () => {
    try {
      await updateSite(site.name, siteFormState.maximum);
      showToaster(`Site "${site.name}" ble oppdatert!`, {
        type: "success",
      });

      // Update the site object and notify parent if onChange is provided
      if (onChange) {
        const updatedSite = {
          ...site,
          maximum: siteFormState.maximum,
        };
        onChange(updatedSite);
      }

      setSiteFormState((prevState) => ({
        ...prevState,
        isDirty: false,
      }));
    } catch (error) {
      handleError(error, "Kunne ikke oppdatere site", "oppdatering av site");
    }
  };

  const handleResetForm = () => {
    setSiteFormState(resetSiteFormState());
  };

  return (
    <div className="sites-admin-form">
      <div className="horizontal-line" />
      <div className="form site-form">
        <SectionHeader icon={LuArrowUpToLine} title="Site-innstillinger" />
        <p>Her kan du endre maksimumsverdien for siten.</p>
        <div className="form-group">
          <label>Maksimum</label>
          <input
            type="number"
            min="0"
            value={siteFormState.maximum}
            onChange={(e) =>
              updateFormField("maximum", parseInt(e.target.value) || 0)
            }
          />
        </div>
        <div className="actions">
          <Button
            disabled={!siteFormState.isDirty}
            className="reset-button"
            buttonStyle="outline"
            onClick={handleResetForm}
          >
            <RxReset />
          </Button>
          <Button
            className="save-button"
            disabled={!siteFormState.isDirty}
            onClick={handleSaveSiteChanges}
          >
            Lagre endringer
          </Button>
        </div>
      </div>
      <div className="horizontal-line" />
      <SiteResetTimeField siteName={site.name} />
    </div>
  );
};

export default SitesAdminForm;
